import { debug } from '@byted/tiktok-zti-utils';

// === 工具函数 ===
const buf2b64 = (buf: ArrayBuffer): string => btoa(String.fromCharCode(...new Uint8Array(buf)));

const b642buf = (b64: string): Uint8Array => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

const ivKey = `tt-ticket-guard-iv`; // 固定的 IV 密钥前缀

// === 密钥派生函数 ===
const deriveKey = async (passphrase: string): Promise<CryptoKey> => {
  debug('[deriveKey] Deriving key from:', passphrase);
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey('raw', encoder.encode(passphrase), 'PBKDF2', false, [
    'deriveKey'
  ]);
  return await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode('secure-salt'), // 固定盐值
      iterations: 1000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 128 },
    false,
    ['encrypt', 'decrypt']
  );
};

// === 加密函数 ===
export const encryptText = async (text: string): Promise<string | null> => {
  const start = performance.now();
  try {
    const key = await deriveKey(ivKey);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(text);
    const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);

    // 拼接 iv + 密文
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encrypted), iv.length);

    const base64 = buf2b64(combined.buffer);
    debug(`[encryptText] 耗时 ${(performance.now() - start).toFixed(2)} ms`);
    return base64;
  } catch (err) {
    debug('[encryptText] 加密失败：', err);
    return null;
  }
};

// === 解密函数 ===
export const decryptText = async (base64Str: string): Promise<string | null> => {
  const start = performance.now();
  try {
    const combined = b642buf(base64Str);
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);

    const key = await deriveKey(ivKey);
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
    const text = new TextDecoder().decode(decrypted);

    debug(`[decryptText] 耗时 ${(performance.now() - start).toFixed(2)} ms`);
    return text;
  } catch (err) {
    debug('[decryptText] 解密失败：', err);
    return null;
  }
};
